import './_refill.scss';
import React, { useEffect, createContext, useReducer, useContext } from 'react';
import { Grid } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import qs from 'qs';

import { isEqual } from 'lodash';
import PageNotFound from '../../404';
import FilterGroup from '../../../components/Filters/FilterGroup';
import SearchGroup from '../../../components/Search/SearchGroup';
import Spinner from '../../../components/Loaders/Spinner';
import Tabs from '../../../components/Tabs';
import Pagination from '../../../components/Pagination';
import RefillTable from './components/RefillTable';
import Loader from '../../../components/Loaders/Loader';
import Modals from './components/Modals';

import { getNavbarCountersAction } from '../../../redux/actions/header';
import { getRefillSelector } from '../../../redux/selectors/refill';
import {
  changeChecked,
  mainReducer,
  mainState,
  modalOpen,
  listSuccess,
  updateListError,
  loadingTable,
  loadingTablePagination,
  updateListSuccess,
  updatePaginationSuccess,
} from './reducer';
import { getErrorMessage, pageWrapperScrollTop } from '../../../utils/helper';
import { refillConfiguration, refillList, refillListPagination } from '../../../services/endpoints/refill';
import * as modals from './constants/modals';
import { clearRootStateAction } from '../../../redux/actions/root';
import { WebsocketContext } from '../../../utils/WebsocketProvider';

export const MainContext = createContext({});
const Refill = () => {
  const { eventData } = useContext(WebsocketContext);

  const dispatch = useDispatch();

  const [mainStore, mainDispatch] = useReducer(mainReducer, mainState);
  const {
    isInitPage,
    initPageError,
    statuses,
    tasks,
    checked,
    configuration,
    pagination,
    search_types,
    updateList,
    isLoadingTable,
    isLoadingPagination,
  } = mainStore;

  const { isFetching, modesFetch, servicesFetch } = useSelector((state) => getRefillSelector(state));

  const location = useLocation();
  const queryString = qs.parse(location.search.slice(1));

  const initPage = async () => {
    try {
      /**
       * 1 - Проверка доступтов в запросе config
       */
      const configuration = await refillConfiguration();
      const access = configuration?.data?.access;
      if (!access || !access.list) {
        throw new Error('Error access');
      }
      const requestPull = [await refillList(queryString)];

      const [respList] = await Promise.all(requestPull);
      const payload = {
        configuration: {
          access: configuration.data.access,
        },
        search_types: configuration.data.search_types,
        services: configuration.data.services,
        modes: configuration.data.modes,
        statuses: configuration.data.statuses,
        ...respList.data,
      };
      mainDispatch(listSuccess(payload));
      /**
       * 2 - Проверка, нужен ли запрос пагинации
       */
      if (respList.data.need_pagination) {
        mainDispatch(loadingTablePagination());
        const responsePagination = await refillListPagination(queryString);
        const pagination = responsePagination.data.pagination;
        mainDispatch(updatePaginationSuccess(pagination));
      }
    } catch (error) {
      const data = getErrorMessage(error);
      const status = error.status ? error.status : 500;
      const statusText = error.statusText ? error.statusText : 'Unknown error';
      mainDispatch(updateListError(data, status, statusText));
    }
  };

  const updateListing = async () => {
    try {
      const response = await refillList(queryString);
      const isUpdatePagination = !isEqual(response.data?.filters, mainStore?.filters);
      const payload = {
        ...response.data,
        pagination: isUpdatePagination
          ? response.data.pagination
          : {
            ...mainStore.pagination,
            current_page: response.data.pagination.current_page,
          },
      };
      mainDispatch(updateListSuccess(payload));
      /**
       * 1 - Проверка, нужен ли запрос пагинации
       */
      if (response.data.need_pagination && isUpdatePagination) {
        mainDispatch(loadingTablePagination());
        const responsePagination = await refillListPagination(queryString);
        const pagination = responsePagination.data.pagination;
        mainDispatch(updatePaginationSuccess(pagination));
      }
    } catch (error) {
      const data = getErrorMessage(error);
      const status = error?.status ? error.status : 500;
      const statusText = error?.statusText ? error.statusText : 'Unknown error';
      mainDispatch(updateListError(data, status, statusText));
    }
  };

  useEffect(() => {
    if (updateList) {
      updateListing();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updateList]);

  useEffect(() => {
    dispatch(getNavbarCountersAction({ code: 'refill-list' }));
    if (!isInitPage) {
      initPage();
    }
    document.title = 'Refill';
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isInitPage) {
      mainDispatch(loadingTable());
      updateListing();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  useEffect(() => {
    if (isInitPage && queryString.page) {
      pageWrapperScrollTop();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryString.page]);

  useEffect(
    () => () => {
      dispatch(clearRootStateAction());
    },
    [dispatch],
  );

  useEffect(() => {
    switch (eventData?.name) {
    case 'menu_counter_update':
      if (eventData?.options?.code === 'refill-list') {
        updateListing();
      }
      break;

    default:
      break;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventData]);

  const onChangeStatus = ({ id, key, actionType }) => {
    switch (actionType) {
    case 'single':
      mainDispatch(
        modalOpen(modals.CHANGE_STATUS, {
          id,
          status_id: key,
        }),
      );
      break;
    case 'mass':
      mainDispatch(
        modalOpen(modals.CHANGE_MASS_STATUS, {
          id,
          status_id: key,
        }),
      );
      break;
    default:
    }
  };

  const onOpenDetails = (id) => {
    if (id) {
      mainDispatch(
        modalOpen(modals.DETAILS, {
          id,
        }),
      );
    }
  };

  const onOpenSuperAdminDetails = (id) => {
    if (id) {
      mainDispatch(
        modalOpen(modals.SUPER_ADMIN_DETAILS, {
          id,
        }),
      );
    }
  };

  const onChangeChecked = (newChecked) => {
    mainDispatch(changeChecked(newChecked));
  };

  if (!isInitPage) {
    return <Loader />;
  }

  if (isInitPage && initPageError) {
    return <PageNotFound errorDataMessage={initPageError?.data} errorStatus={initPageError?.status} errorStatusText={initPageError?.statusText} />;
  }

  return (
    <MainContext.Provider value={[mainStore, mainDispatch]}>
      <Grid fluid>
        <FilterGroup>
          {statuses && !isFetching && <Tabs tab="status" tabsData={statuses} />}
          <SearchGroup autocomplete="on" sectionName="refill" withSelect options={search_types} placeholder="Search" />
        </FilterGroup>

        {isFetching ? (
          <div className="modal-loader" />
        ) : (
          <>
            {configuration?.access?.list && (
              <RefillTable
                values={checked}
                setValues={onChangeChecked}
                config={{ ...configuration, statuses }}
                tasks={tasks}
                access={configuration?.access}
                toggleView={onOpenDetails}
                setConfirmValues={onChangeStatus}
                toggleSuperDetail={onOpenSuperAdminDetails}
                isLoadingTable={isLoadingTable}
                modesFetch={modesFetch}
                servicesFetch={servicesFetch}
              />
            )}
            <>
              {isLoadingPagination ? (
                <div className="pagination">
                  <Spinner />
                </div>
              ) : (
                <Pagination
                  pages={pagination?.pages}
                  currentPage={pagination?.current_page}
                  itemsCount={pagination?.total_count}
                  pageSize={pagination?.page_size}
                  pagesLimit={pagination?.pages < 10 ? pagination?.pages : 10}
                  withPageSizer
                />
              )}
            </>
          </>
        )}
      </Grid>
      <Modals />
    </MainContext.Provider>
  );
};

export default Refill;
